import { useState, useMemo, useEffect } from 'react'
import { Player } from '../types'
import { useAuthStore, usePlayerStore } from '../stores'
import { useVideoStore } from '../stores/useVideoStore'
import { useProStatus } from '../utils/useProStatus'
import {
  Video, Plus, X, Trash2, Loader2, AlertCircle, CheckCircle2,
  Calendar, MapPin, Clock, Trophy, Filter, ExternalLink, Target, TrendingUp
} from 'lucide-react'

const POSITIONS = ['GK', 'CB', 'RB', 'LB', 'RWB', 'LWB', 'CDM', 'CM', 'CAM', 'LW', 'RW', 'SS', 'CF']
const MATCH_RESULTS = ['Win', 'Draw', 'Loss']

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  return null
}

interface PlayerMatchClipsProps {
  player?: Player
}

function PlayerMatchClips({ player: playerProp }: PlayerMatchClipsProps) {
  const { user } = useAuthStore()
  const { players } = usePlayerStore()
  const { videos, loading, error, fetchVideos, addVideo, deleteVideo } = useVideoStore()
  const { isPro } = useProStatus()
  const atLimit = !isPro && videos.length >= 1
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [playingVideo, setPlayingVideo] = useState<string | null>(null)

  const [filterSeason, setFilterSeason] = useState('All')
  const [filterCompetition, setFilterCompetition] = useState('All')
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest')

  const [formData, setFormData] = useState({
    url: '',
    opponent: '',
    competition: '',
    match_date: '',
    position_played: '',
    minutes_played: '',
    match_result: '',
    goals: '',
    assists: '',
    shots: '',
    key_passes: '',
    dribbles: '',
    tackles: '',
  })

  const currentPlayer = useMemo(() => {
    if (playerProp) return playerProp
    if (!user) return null
    return players.find(p =>
      p.scout?.toLowerCase() === user.email?.toLowerCase() ||
      p.email?.toLowerCase() === user.email?.toLowerCase() ||
      p.name?.toLowerCase() === user.name?.toLowerCase()
    ) || null
  }, [playerProp, user, players])

  const isOwner = useMemo(() => {
    if (!user || !currentPlayer) return false
    return (
      currentPlayer.scout?.toLowerCase() === user.email?.toLowerCase() ||
      currentPlayer.email?.toLowerCase() === user.email?.toLowerCase() ||
      currentPlayer.id === user.id
    )
  }, [user, currentPlayer])

  useEffect(() => {
    if (currentPlayer?.id) {
      fetchVideos(currentPlayer.id)
    }
  }, [currentPlayer?.id, fetchVideos])

  const youtubeId = useMemo(() => extractYouTubeId(formData.url), [formData.url])

  const seasons = useMemo(() => {
    const years = new Set<string>()
    videos.forEach(v => {
      if (v.match_date) {
        years.add(new Date(v.match_date).getFullYear().toString())
      }
    })
    return ['All', ...Array.from(years).sort().reverse()]
  }, [videos])

  const competitions = useMemo(() => {
    const names = new Set<string>()
    videos.forEach(v => {
      if (v.competition) names.add(v.competition)
    })
    return ['All', ...Array.from(names).sort()]
  }, [videos])

  const filteredVideos = useMemo(() => {
    let result = [...videos]

    if (filterSeason !== 'All') {
      result = result.filter(v => {
        if (!v.match_date) return false
        return new Date(v.match_date).getFullYear().toString() === filterSeason
      })
    }

    if (filterCompetition !== 'All') {
      result = result.filter(v => v.competition === filterCompetition)
    }

    result.sort((a, b) => {
      const dateA = a.match_date || a.created_at || ''
      const dateB = b.match_date || b.created_at || ''
      return sortBy === 'newest'
        ? new Date(dateB).getTime() - new Date(dateA).getTime()
        : new Date(dateA).getTime() - new Date(dateB).getTime()
    })

    return result
  }, [videos, filterSeason, filterCompetition, sortBy])

  const resetForm = () => {
    setFormData({
      url: '',
      opponent: '',
      competition: '',
      match_date: '',
      position_played: '',
      minutes_played: '',
      match_result: '',
      goals: '',
      assists: '',
      shots: '',
      key_passes: '',
      dribbles: '',
      tackles: '',
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentPlayer?.id || !formData.url) return

    if (!youtubeId) {
      return
    }

    setSaving(true)
    try {
      await addVideo({
        player_id: currentPlayer.id,
        uploaded_by: user?.id,
        title: `${formData.opponent || 'Match'} - ${formData.competition || 'Clip'}`,
        url: formData.url,
        platform: 'youtube',
        thumbnail_url: `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`,
        opponent: formData.opponent,
        competition: formData.competition,
        match_date: formData.match_date || undefined,
        position_played: formData.position_played,
        minutes_played: formData.minutes_played ? parseInt(formData.minutes_played) : undefined,
        match_result: formData.match_result,
        goals: formData.goals ? parseInt(formData.goals) : undefined,
        assists: formData.assists ? parseInt(formData.assists) : undefined,
        shots: formData.shots ? parseInt(formData.shots) : undefined,
        key_passes: formData.key_passes ? parseInt(formData.key_passes) : undefined,
        dribbles: formData.dribbles ? parseInt(formData.dribbles) : undefined,
        tackles: formData.tackles ? parseInt(formData.tackles) : undefined,
      })
      resetForm()
      setShowForm(false)
      setSuccess('Match clip added!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Failed to add clip:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteVideo(id)
      setDeleteConfirm(null)
      setSuccess('Clip deleted!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Failed to delete clip:', err)
    }
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">{isOwner ? 'My Match Clips' : 'Match Clips'}</h2>
          <p className="text-sm text-gray-400 mt-1">YouTube highlights from matches</p>
        </div>
        {isOwner && (
          atLimit ? (
            <a href="/pricing" className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-yellow-400 hover:bg-yellow-500/20 transition text-xs font-bold uppercase tracking-widest">
              <Plus size={14} />
              Upgrade for More Clips
            </a>
          ) : (
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400 hover:bg-green-500/20 transition text-xs font-bold uppercase tracking-widest"
            >
              {showForm ? <X size={14} /> : <Plus size={14} />}
              {showForm ? 'Cancel' : 'Add Clip'}
            </button>
          )
        )}
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-700/30 rounded-xl text-green-400 text-xs font-bold">
          <CheckCircle2 size={16} />
          {success}
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-700/30 rounded-xl text-red-400 text-xs font-bold">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Add Clip Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 space-y-4">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider">Add Match Clip</h3>

          <div className="space-y-1">
            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">YouTube Link *</label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
              placeholder="https://www.youtube.com/watch?v=..."
              required
              className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
            />
            {formData.url && !youtubeId && (
              <p className="text-[10px] text-red-400 uppercase tracking-widest">Invalid YouTube URL</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Opponent</label>
              <input
                type="text"
                value={formData.opponent}
                onChange={(e) => setFormData(prev => ({ ...prev, opponent: e.target.value }))}
                placeholder="e.g. FC United"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Competition</label>
              <input
                type="text"
                value={formData.competition}
                onChange={(e) => setFormData(prev => ({ ...prev, competition: e.target.value }))}
                placeholder="e.g. NPFL"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Match Date</label>
              <input
                type="date"
                value={formData.match_date}
                onChange={(e) => setFormData(prev => ({ ...prev, match_date: e.target.value }))}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Position Played</label>
              <select
                value={formData.position_played}
                onChange={(e) => setFormData(prev => ({ ...prev, position_played: e.target.value }))}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
              >
                <option value="" className="bg-gray-900">Select...</option>
                {POSITIONS.map(pos => (
                  <option key={pos} value={pos} className="bg-gray-900">{pos}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Minutes Played</label>
              <input
                type="number"
                value={formData.minutes_played}
                onChange={(e) => setFormData(prev => ({ ...prev, minutes_played: e.target.value }))}
                placeholder="e.g. 90"
                min="0"
                max="120"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
              />
            </div>
            <div className="space-y-1">
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Match Result</label>
              <select
                value={formData.match_result}
                onChange={(e) => setFormData(prev => ({ ...prev, match_result: e.target.value }))}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/5 rounded-xl text-white focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
              >
                <option value="" className="bg-gray-900">Select...</option>
                {MATCH_RESULTS.map(result => (
                  <option key={result} value={result} className="bg-gray-900">{result}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Match Stats */}
          <div className="space-y-2">
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Match Stats</p>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              <div className="space-y-1">
                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Goals</label>
                <input
                  type="number"
                  value={formData.goals}
                  onChange={(e) => setFormData(prev => ({ ...prev, goals: e.target.value }))}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Assists</label>
                <input
                  type="number"
                  value={formData.assists}
                  onChange={(e) => setFormData(prev => ({ ...prev, assists: e.target.value }))}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Shots</label>
                <input
                  type="number"
                  value={formData.shots}
                  onChange={(e) => setFormData(prev => ({ ...prev, shots: e.target.value }))}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Key Passes</label>
                <input
                  type="number"
                  value={formData.key_passes}
                  onChange={(e) => setFormData(prev => ({ ...prev, key_passes: e.target.value }))}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Dribbles</label>
                <input
                  type="number"
                  value={formData.dribbles}
                  onChange={(e) => setFormData(prev => ({ ...prev, dribbles: e.target.value }))}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest">Tackles</label>
                <input
                  type="number"
                  value={formData.tackles}
                  onChange={(e) => setFormData(prev => ({ ...prev, tackles: e.target.value }))}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm font-bold"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => { setShowForm(false); resetForm() }}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-gray-400 hover:text-white transition text-xs font-bold uppercase tracking-widest"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !youtubeId}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 border border-green-500 rounded-xl text-black hover:bg-green-400 transition text-xs font-bold uppercase tracking-widest disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {saving ? 'Adding...' : 'Add Clip'}
            </button>
          </div>
        </form>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase tracking-widest">
          <Filter size={14} />
          Filters:
        </div>
        <select
          value={filterSeason}
          onChange={(e) => setFilterSeason(e.target.value)}
          className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-xs font-bold focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none"
        >
          {seasons.map(s => (
            <option key={s} value={s} className="bg-gray-900">{s === 'All' ? 'All Seasons' : s}</option>
          ))}
        </select>
        <select
          value={filterCompetition}
          onChange={(e) => setFilterCompetition(e.target.value)}
          className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-xs font-bold focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none"
        >
          {competitions.map(c => (
            <option key={c} value={c} className="bg-gray-900">{c === 'All' ? 'All Competitions' : c}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest')}
          className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white text-xs font-bold focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none"
        >
          <option value="newest" className="bg-gray-900">Newest First</option>
          <option value="oldest" className="bg-gray-900">Oldest First</option>
        </select>
        <span className="text-[10px] text-gray-600 uppercase tracking-widest">
          {filteredVideos.length} clip{filteredVideos.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-green-500 animate-spin" size={24} />
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredVideos.length === 0 && (
        <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-8 text-center">
          <Video className="mx-auto text-gray-500 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white">No Match Clips</h3>
          <p className="text-gray-400 mt-2 text-sm">
            {videos.length === 0
              ? (isOwner ? 'Add your first match clip by pasting a YouTube link.' : 'No clips available yet.')
              : 'No clips match your current filters.'}
          </p>
        </div>
      )}

      {/* Video Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredVideos.map((video) => (
          <div key={video.id} className="bg-[#161b22] border border-gray-800 rounded-2xl overflow-hidden">
            {/* YouTube Embed */}
            <div className="relative aspect-video bg-black">
              {video.thumbnail_url ? (
                <img
                  src={video.thumbnail_url}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                  <Video size={32} className="text-gray-600" />
                </div>
              )}
              <button
                type="button"
                onClick={() => setPlayingVideo(extractYouTubeId(video.url) || video.url)}
                className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition group"
              >
                <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center group-hover:scale-110 transition">
                  <svg viewBox="0 0 24 24" className="w-8 h-8 text-white fill-current ml-1">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </button>
            </div>

            {/* Match Details */}
            <div className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{video.title}</p>
                  {video.opponent && (
                    <p className="text-xs text-gray-400 mt-1">vs {video.opponent}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-2">
                  {video.match_result && (
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                      video.match_result === 'Win' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                      video.match_result === 'Draw' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' :
                      'bg-red-500/10 text-red-400 border border-red-500/20'
                    }`}>
                      {video.match_result}
                    </span>
                  )}
                  {isOwner && (
                    deleteConfirm === video.id ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDelete(video.id)}
                          className="text-[10px] text-red-400 hover:text-red-300 font-bold uppercase tracking-widest"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-[10px] text-gray-500 hover:text-gray-400 font-bold uppercase tracking-widest"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirm(video.id)}
                        className="p-1.5 text-gray-500 hover:text-red-400 transition"
                      >
                        <Trash2 size={14} />
                      </button>
                    )
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 text-[10px] text-gray-500 uppercase tracking-widest">
                {video.competition && (
                  <span className="flex items-center gap-1">
                    <Trophy size={10} className="text-green-500" />
                    {video.competition}
                  </span>
                )}
                {video.match_date && (
                  <span className="flex items-center gap-1">
                    <Calendar size={10} className="text-blue-500" />
                    {new Date(video.match_date).toLocaleDateString()}
                  </span>
                )}
                {video.position_played && (
                  <span className="flex items-center gap-1">
                    <MapPin size={10} className="text-purple-500" />
                    {video.position_played}
                  </span>
                )}
                {video.minutes_played && (
                  <span className="flex items-center gap-1">
                    <Clock size={10} className="text-orange-500" />
                    {video.minutes_played}'
                  </span>
                )}
              </div>

              {/* Stat Badges */}
              {video.goals != null && video.goals > 0 && (
                <div className="flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-widest">
                  {video.goals > 0 && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
                      <Target size={10} />
                      {video.goals} G
                    </span>
                  )}
                  {video.assists != null && video.assists > 0 && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                      <TrendingUp size={10} />
                      {video.assists} A
                    </span>
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={() => setPlayingVideo(extractYouTubeId(video.url) || video.url)}
                className="flex items-center gap-1 text-[10px] text-green-400 hover:text-green-300 font-bold uppercase tracking-widest"
              >
                <ExternalLink size={10} />
                Watch Video
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Video Player Modal */}
      {playingVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPlayingVideo(null)}
        >
          <div
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPlayingVideo(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition"
            >
              <X size={24} />
            </button>
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${playingVideo}?autoplay=1&rel=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
                title="Match Clip"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PlayerMatchClips
